<template>
  <div id="sale">

    <PageTitle :title="`${$t('sales.sale')} #${sale ? sale.id : ''}`" :subtitle="$t('sales.subtitle')"></PageTitle>

    <div class="container">
      <template v-if="sale && sale.posFailed && !isPosSale">
        <TipBox danger class="sale__warning">
          <div class="inner">
            <span class="warning-text" v-html="$t('sales.pos_failed')"></span>
            <button class="btn btn-primary" @click="resendToPos">{{ $t('sales.resend_pos.resend') }}</button>
          </div>
        </TipBox>
        <TipBox danger class="sale__warning">
          <div class="inner">
            <span class="warning-text bottom" v-html="$t('sales.pos_failed_set_pos_status')"></span>
            <button class="btn btn-primary" @click="setSaleAsImported" disabled>{{ $t('sales.import_pos_manually.set') }}</button>
          </div>
        </TipBox>
      </template>

      <Panel :title="$t('sales.advanced_information')"
             v-if="sale && ($store.state.loggedUser.isSuperAdmin || $store.state.loggedUser.isDistributor)">
        <InformationBlock icon="/icon/settings.svg" :data="adminInfo"/>
        <Panel title="POS Response" collapsible collapsed v-show="sale.moreData">
          <json-viewer :value="sale.moreData"/>
        </Panel>
        <Panel title="Sale Response" collapsible collapsed v-show="sale">
          <json-viewer :value="sale"/>
        </Panel>
      </Panel>

      <Panel :title="$t('sales.catering_section.title')" v-if="sale && isCatering(sale)">
        <template v-if="sale && isCatering(sale)">
          <template v-if="sale.deliveryData.questions && sale.deliveryData.questions.length > 0">
            <InformationBlock :title="$t('sales.catering_section.questions')" icon="/icon/help.svg"
                :data="sale.deliveryData.questions.map(q => ({label: q.Q, value: q.A}))"
            />
          </template>
          <div class="columns">
            <div class="column">
              <div class="group">
                <label>{{ $t('sales.catering_section.note') }}</label>
                <textarea v-model="sale.deliveryData.note"/>
              </div>
            </div>
            <div class="column">
              <div class="group">
                <label>{{ $t('sales.catering_section.status') }}</label>
                <select v-model="sale.processingStatus">
                  <option :value="null">{{ $t('none_m') }}</option>
                  <option :value="ps.id" v-for="ps of processingStatuses">{{ $to(ps.name) }}</option>
                </select>
              </div>
            </div>
          </div>
          <div class="buttons">
            <button class="btn btn-primary" @click="saveCatering">{{ $t('save') }}</button>
            <button class="btn btn-primary" @click="print">{{ $t("print") }}</button>
            <button class="btn btn-primary" @click="resendToPos"
                    v-if="sale.holdAndFire" :disabled="sale.holdAndFire.status !== 'fired_failed'">
              {{ $t('sales.resend_pos.resend') }}
            </button>
          </div>
        </template>
      </Panel>

      <Panel :title="$t('branch.contact.title')" v-if="sale">
        <div class="columns">
          <div class="column" v-if="sale.contact">
            <InformationBlock :title="$t('branch.contact.title')" icon="/icon/user.svg" :data="[
              {label: $t('fullName'), value: (sale.contact.firstName || sale.contact.lastName) ? sale.contact.firstName + ' ' + sale.contact.lastName : $t('not_available') },
              {label: $t('phoneNumber'), value: sale.contact.phone},
              {label: $t('email'), value: sale.contact.email}
            ]"/>
          </div>
          <div class="column" v-if="sale.deliveryData && sale.deliveryData.fullAddress">
            <InformationBlock :title="$t('sales.location')" icon="/icon/location.svg" :data="[
              {label: 'Adresse', value: sale.deliveryData.fullAddress},
              {label: 'Appartement', value: sale.deliveryData.app},
              {label: 'Code postal', value: sale.deliveryData.zip}
            ]"/>
          </div>
        </div>
        <div class="buttons">
          <button class="btn btn-primary" @click="viewCustomer">{{ $t('customer.go_to') }}</button>
          <button class="btn btn-primary" @click="resendNotificationModal">{{ $t('sales.notification.resend') }}
          </button>
          <button class="btn btn-primary" @click="resendToPos" v-if="sale.holdAndFire"
                  :disabled="sale.holdAndFire.status === 'fired_failed'">{{ $t('sales.resend_pos.resend') }}
          </button>
        </div>
      </Panel>

      <Panel :title="$t('sales.order_details')" v-if="sale">
        <div class="columns">
          <div class="column">
            <InformationBlock :title="$t('sales.order_details')" icon="/icon/devices.svg" :data="[
              {label: $t('sales.order_platform'), value: salePlatform + (isPosSale ?  ' (' + $t('services.qr_payment') + ')' : '')},
              {label: $t('sales.order_type'), value: $t(`deliveryMethod.${sale.deliveryMethod}`)},
              sale.kioskNumber ? {label: $t('sales.kiosk_order_number'), value: sale.kioskNumber} : undefined,
              (sale.deliveryData && sale.deliveryData.tableNumber) ? {label: $t('sales.table_number'), value: sale.deliveryData.tableNumber} : undefined,
              (sale.deliveryData && sale.deliveryData.roomNumber) ? {label: $t('sales.room_number'), value: sale.deliveryData.roomNumber} : undefined,
              {label: $t('sales.receipt_number'), value: sale.id},
              {label: $t('sales.comment'), value: sale.userComment}
            ]">
            </InformationBlock>
          </div>
          <div class="column">
            <InformationBlock :title="$t('sales.date_hour')" icon="/icon/time.svg" :data="[
              {label: $t('sales.order_hour'), value: formatDateTime(sale.completedAt)},
              {label: $t('sales.reception_hour'), value: formatDateTime(sale.inAdvanceFor || sale.companyCreatedAt)}
            ]">
            </InformationBlock>
          </div>
        </div>
        <InformationBlock v-if="sale.shipperData" :title="$t('settings.shipper')" icon="/icon/delivery-car.svg" :data="[
              //{label: $t('settings.shipper'), value: formatDateTime(sale.shipperData)},
              {label: $t('sales.shipper.name'), value: $t(sale.shipperData.shipper)},
              {label: $t('sales.shipper.id'), value: sale.shipperData.id},
              {label: $t('sales.shipper.estimated_pickup'), value: formatDateTime(sale.shipperData.estimated_pickup_time)},
              {label: $t('sales.shipper.estimated_delivery'), value: formatDateTime(sale.shipperData.estimated_delivery_time)},
              {label: $t('sales.shipper.tracking'), value: sale.shipperData.delivery_tracking_url, url: true}
            ]">
        </InformationBlock>
      </Panel>

      <Panel :title="$t('sales.payment_information')" v-if="sale">
        <div class="columns">
          <!-- When POS, show invoices and seats -->
          <div class="column invoices" v-if="isSeatedOrder">
            <template v-for="(invoice, i) of sale.invoices">
              <label class="invoiceLabel">Invoice {{ invoice.externalId }}</label>
              <div class="invoiceBlock">
                <div class="seatBlock" v-for="(seat, j) of invoice.seats" :key="`seat${i}-${j}`">
                  <label>Seat {{ seat.id }}</label>
                  <div class="simpleItemLine" v-for="(item, k) of seat.items" :key="`item${i}-${j}-${k}`">
                    <div>{{ item.name }}<span v-if="item.quantity > 1"> x{{ item.quantity }}</span></div>
                    <div class="value" v-if="Math.abs(item.quantity*item.unitPrice) > 0.001">
                      {{ $tc(item.quantity * item.unitPrice, sale.currency) }}
                    </div>
                  </div>
                </div>
                <div class="simpleItemLine big">
                  <div>{{ $t('sales.subtotal') }}</div>
                  <div class="value">{{ $tc(invoice.subTotal, sale.currency) }}</div>
                </div>
                <div class="simpleItemLine" v-for="tax of invoice.taxes">
                  <div>{{ tax.name }}</div>
                  <div class="value">{{ $tc(tax.total, sale.currency) }}</div>
                </div>
                <div class="simpleItemLine big">
                  <div>{{ $t('sales.total') }}</div>
                  <div class="value">{{ $tc(invoice.total, sale.currency) }}</div>
                </div>
                <div class="simpleItemLine" v-if="invoice.tipTotal">
                  <div>{{ $t('sales.tip') }}</div>
                  <div class="value">{{ $tc(invoice.tipTotal, sale.currency) }}</div>
                </div>
                <div class="simpleItemLine" v-if="invoice.serviceFees">
                  <div>{{ $t('sales.service_fees') }}</div>
                  <div class="value">{{ $tc(invoice.serviceFees, sale.currency) }}</div>
                </div>
                <div class="simpleItemLine" v-if="invoice.surchargeFees">
                  <div>{{ $t('sales.surcharge_fees') }}</div>
                  <div class="valie">{{ $tc(invoice.surchargeFees, sale.currency) }}</div>
                </div>
              </div>
            </template>
          </div>

          <!-- When online ordering, old design -->
          <div class="column" v-else>
            <label class="title" style="margin-bottom: 10px">{{ $t('sales.products') }}</label>
            <div class="itemBlock" v-for="detail of saleItems" :key="detail.id">
              <!-- <div class="image"></div> -->
              <div class="info">
                <div class="top">
                  <div class="name">{{ detail.name }}<span v-if="detail.quantity > 1"> x{{ detail.quantity }}</span>
                  </div>
                  <div class="price" v-if="detail.totalPrice">{{ $tc(detail.totalPrice, sale.currency) }}</div>
                </div>
                <div class="extras">
                  <div class="extra" v-for="extra of detail.extras">
                    <div class="name">{{ extra.name }} x{{ extra.quantity }}</div>
                    <div class="price">{{ $tc(extra.unitPrice * extra.quantity) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="column">
            <InformationBlock :title="$t('sales.supplements')" icon="/icon/receipt.svg" :data="[
                sale.deliveryFees > 0 ? {label: $t('sales.delivery_fees'), value: this.$tc(sale.deliveryFees, sale.currency)} : undefined,
                sale.serviceFees > 0 ? {label: $t('sales.service_fees'), value: this.$tc(sale.serviceFees, sale.currency)} : undefined,
                ...(sale.promotions || []).map(p => ({label: p.name, value: this.$tc(p.total, sale.currency)})),
                {label: $t('sales.subtotal'), value: this.$tc(sale.subTotal, sale.currency), options: {bigger: true}},
                ...(sale.taxes || []).map(t => ({label: t.name, value: this.$tc(t.total, sale.currency)})),
                sale.tipTotal ? {label: $t('sales.tip'), value: this.$tc(sale.tipTotal, sale.currency)} : undefined,
                {label: $t('sales.total'), value: this.$tc(sale.total, sale.currency), options: {bigger: true}}
              ]">
            </InformationBlock>
            <InformationBlock :title="$t('sales.payments')" icon="/icon/wallet.svg" :data="formattedPayments">
            </InformationBlock>
            <div>
              <!--
              <button class="btn btn-primary" @click="refundSale" :disabled="!isSaleRefundable"
                      v-if="sale.refundable">{{ $t('refund') }}
              </button> -->
            </div>
            <div v-if="isSaleRefunded">
              <label class="title" style="margin-bottom: 10px">{{ $t('loyalty_transactions.refund') }}</label>
              <div class="refunded itemBlock">
                {{ $t('sales.refunded') }}
              </div>
            </div>
          </div>
        </div>
      </Panel>

      <Panel :title="$t('sales.aims_vouchers')" v-if="aimsVouchers.length > 0">
        <template v-if="aimsVouchers.length > 0">
          <div class="multiple-canvas">
            <div class="canvas-holder" v-for="voucher of aimsVouchers" :key="'canvas-' + voucher.raw">
              <div :id="'canvas-' + voucher.raw"></div>
              <div class="filename" v-if="qrCodes.find(q => q.filename === voucher.raw)">
                {{ voucher.raw }}
                <br><br>
                {{ getSaleDetailName(voucher.detail) }}
              </div>
            </div>
          </div>
          <br>
          <br>
          <div class="footer-actions">
            <button class="btn btn-primary" @click="downloadAllQRCodes">{{
                $t('qr_payment.qr_code.download_all')
              }}
            </button>
          </div>
        </template>
      </Panel>

      <PrintSales :sales="[sale]" ref="print"/>
    </div>

    <Modal name="notif_confirm" :title="$t('sales.notification.confirmation')" dismissable :actions="[
        {name: $t('back'), action: 'close'},
        {name: $t('send'), action: resendNotification, primary: true
      }]">
    </Modal>

  </div>
</template>

<script>

import moment from "moment";
import {mapActions} from "vuex";
import {Core} from "@/core/Core";
import InformationBlock from "@/components/tools/InformationBlock.vue";
import QRCodeStyling from "qr-code-styling";
import PrintSales from "@/components/PrintSales.vue";
import JSZip from "jszip";
import JsonViewer from 'vue-json-viewer'
import PageTitle from "@/components/PageTitle";

export default {
  name: "sale",

  components: {InformationBlock, PrintSales, JsonViewer, PageTitle},

  data() {
    return {
      processingStatuses: [],
      saleId: null,
      isPosSale: false,
      inventoryId: null,
      saleItems: null,
      platform: null,
      sale: null,
      moment: moment,
      qrCodes: []
    }
  },

  computed: {
    formattedPayments() {
      let payments = [];
      for (let payment of this.sale.payments) {
        let toAdd = [
          {
            label: this.$t('sales.payment_type'),
            value: payment.processor === 'none' ? payment.method : payment.processor
          },
          {label: this.$t('sales.amount'), value: this.$tc(payment.amount, payment.currency)}
        ];
        payments = [...payments, ...toAdd];
      }
      return payments;
    },
    aimsVouchers() {
      if (!this.sale || !this.sale.moreData || !this.sale.moreData.aims_vouchers) {
        return [];
      }
      return this.sale.moreData.aims_vouchers;
    },
    hourFormat() {
      if (this.$store.state.contextCompany) {
        return this.$store.state.contextCompany.hourFormat;
      }
      return "24";
    },
    salePlatform() {
      if (!this.sale) {
        return;
      }
      switch (this.sale.appName) {
        case "ishopv3-kiosk": //v3 kiosk sale
          return this.$t('sales.kiosk');
        case "ishopv3-app": //v3 mobile app sale
          return this.$t('sales.app');
        case "ishopv3-qrpay": //v3 qrpay app sale
          return this.$t('sales.qrPay');
        case "ishopv3-web": //v3 online ordering sale
        default:
          if (this.sale.kioskNumber) { //v2 adaptor kiosk sale
            return this.$t('sales.kiosk');
          } else { //not logged sales
            return this.$t('sales.online_order');
          }
      }
    },
    adminInfo() {
      let info = [
        {label: this.$t('sales.ip'), value: this.sale.ip || this.$t('not_available')},
        {label: this.$t('sales.os'), value: this.sale.os || this.$t('not_available')},
        {label: this.$t('app_name'), value: this.sale.appName || this.$t('not_available')},
        {label: this.$t('app_version'), value: this.sale.appVersion || this.$t('not_available')},
        {label: this.$t('timezone'), value: this.sale.timezone || this.$t('not_available')},
        {label: this.$t('language'), value: this.sale.language || this.$t('not_available')},
        {label: this.$t('sales.user_agent'), value: this.sale.userAgent || this.$t('not_available')}
      ];
      if (this.sale.holdAndFire && this.sale.holdAndFire.results) {
        info.push(
            ...this.sale.holdAndFire.results.map((r, i) => ({
              label: this.$t('sales.pos_send') + ` ${i}`,
              value: [r.at, (r.success ? this.$t('success') : this.$t('error.title'))].join('<br>')
            }))
        )
      }
      if (this.sale.errors) {
        info.push(
            ...this.sale.errors.map((r, i) => ({
              label: this.$t('error.title') + ` ${i}`,
              value: [r.message, r.exception].filter(e => !!e).join('<br>')
            }))
        )
      }

      return info;
    },
    isSaleRefundable() {
      return !this.isSaleRefunded && !this.isPosSale;
    },
    isSaleRefunded() {
      if (this.sale && typeof this.sale.payments !== 'undefined' && this.sale.payments.length > 0) {
        let refund = this.sale.payments.find(p => p.refunded);
        if (typeof refund !== 'undefined') {
          return true;
        }
      }
      return false;
    },
    company() {
      return this.$store.state.contextCompany;
    },
    isSeatedOrder() {
      return this.sale.invoices
          && this.sale.invoices.length
          && this.sale.invoices.filter(i => !!i.seats).length
    }
  },

  async mounted() {
    this.$store.state.pageTitleTranslationKey = "sales.title";
    this.$store.state.pageIcon = "/icon/sales.svg";

    if (!this.$route.query.id) {
      return;
    }
    this.isPosSale = this.$route.query.id.startsWith('pos');
    this.saleId = this.$route.query.id.replace('pos', '');

    showSpinner();
    await this.loadSale();
    hideSpinner();
  },

  methods: {

    ...mapActions(["getSaleInventoryItems", "getImage", "getSale", "getPosSaleAsSale", "saveSale"]),

    getSaleDetailName(detailId) {
      let detail = this.sale.details.find(d => d.id === detailId);
      if (detail) {
        return detail.name;
      }
      return "";
    },

    async saveCatering() {
      let data = {
        deliveryData: this.sale.deliveryData,
        processingStatus: this.sale.processingStatus
      };
      let sale = await this.saveSale({saleId: this.sale.id, data});
      if (sale) {
        this.sale = sale;
        toast(this.$t("sales.catering_section.save_success"), "success");
      } else {
        1
      }
    },

    getQRCodeOptions(data) {
      let dimension = 300;
      return {
        width: dimension,
        height: dimension,
        data: data,
        dotsOptions: {
          color: "#000000",
          type: "rounded"
        },
        cornersSquareOptions: {
          color: "#000000"
        },
        backgroundOptions: {
          color: "#ffffff"
        },
        imageOptions: {
          crossOrigin: "anonymous",
          margin: 5
        }
      };
    },

    generateMultipleQRCodes() {
      for (let voucher of this.aimsVouchers) {
        let data = voucher.raw;
        let qrCode = this.qrCodes.find(q => q.filename === data);
        let options = this.getQRCodeOptions(data);
        if (!qrCode) {
          let code = new QRCodeStyling(options, true);
          code.append(document.getElementById("canvas-" + data));
          this.qrCodes.push({
            filename: data,
            code: code
          });
        } else {
          qrCode.code.update(options);
        }
      }
    },

    async downloadAllQRCodes() {
      showSpinner();
      let zip = new JSZip();

      for (let voucher of this.aimsVouchers) {
        let canvasHolder = document.getElementById("canvas-" + voucher.raw);
        if (canvasHolder) {
          let canvas = canvasHolder.getElementsByTagName("canvas")[0];
          if (canvas) {
            let data = window.getBase64Data(canvas.toDataURL());
            zip.file(voucher.raw + ".png", data, {base64: true})
          }
        }
      }

      let data = await zip.generateAsync({type: "base64"});
      if (data) {
        window.downloadAsZip("ishopfood__AIMS-Voucher-QR-codes__sale-" + this.sale.id, data);
      } else {
        toast(this.$t("error.download_failed"), "error");
      }
      hideSpinner();
    },

    isCatering(sale) {
      return sale && sale.deliveryMethod === 'catering'
    },

    resendNotificationModal() {
      this.$modal.show("notif_confirm", true);
    },

    formatDateTime(datetime) {
      return window.formatDateTime(this.hourFormat, datetime);
    },

    async resendNotification() {
      this.$modal.show("notif_confirm", false);
      try {
        await Core.request('POST', `/api/sale/${this.sale.id}/retry-notification`)
        toast(this.$t("sales.notification.sent_success"), "success");
      } catch (e) {
        toast(this.$t("sales.notification.sent_fail"), "error");
      }
    },

    async resendToPos() {
      let response = await showConfirmation(this.$t("warning"), this.$t("sales.resend_pos.confirmation"));
      if (!response) {
        return;
      }
      showSpinner();
      try {
        let response = await Core.request('POST', `/api/sale/${this.sale.id}/retry-hold-and-fire`);
        hideSpinner();
        if (response && !response.failed) {
          toast(this.$t("sales.resend_pos.success"), "success");
          if (this.sale.holdAndFire && this.sale.holdAndFire.status) {
            this.sale.holdAndFire.status = 'fired';
          }
          this.sale.posFailed = false;
        } else {
          toast(this.$t("sales.resend_pos.failed"), "error");
          toast(this.$t("error.title") + ' ' + response.error, "error");
        }
      } catch (e) {
        hideSpinner();
        toast(this.$t("error.no_server"), "error");
        console.log(e)
      }
    },

    async loadSale() {
      let augment = 'admin';
      let saleId = this.saleId;
      let sale = this.isPosSale
          ? await this.getPosSaleAsSale({saleId, augment})
          : await this.getSale({saleId, augment});

      if (sale) {
        this.sale = sale;
        if (this.isCatering(sale)) {
          this.processingStatuses = await Core.request('GET', '/api/entity/processing_status/select');
        }
        this.saleItems = await this.buildItems(sale);
        if (sale.deliveryData) {
          //this.initializeMapPlatform();
        }
        if (this.aimsVouchers.length > 0) {
          this.generateMultipleQRCodes();
        }
      }
    },

    async refundSale() {
      try {
        /* TODO add confirmation steps */
        this.$store.commit('showSpinner', true);
        this.sale = this.isPosSale
            ? await Core.request('PUT', `/api/sale_qr_pay/${this.saleId}/refund?augment=admin`)
            : await Core.request('PUT', `/api/sale/${this.saleId}/refund?augment=admin`);
        this.$store.commit('showSpinner', false);
        toast(this.$t('sales.refund_success'), 'success', this.$t('toast.success'));
      } catch (e) {
        this.$store.commit('showSpinner', false);
        toast(this.$t('error.no_server'), 'error');
        /* TODO 401 'unauthorized' */
        /* TODO 400 'not refundable' */
        /* TODO 500 'server error' */
      }
    },

    async buildItems(sale) {
      let items = [];

      for (let item of sale.details) {
        item.ghostItemUsed = item.ghostItem;
        if (item.line.length === 1) {
          item.extras = [];
          item.totalPrice = item.unitPrice;
          items.push(item);
        }
        if (item.line.length > 1) {
          switch (item.type) {
            case "supplement":
            case "variant":
              if (this.sale && this.sale.appName === "uber_eats") {
                items[item.line[0] - 1].name += " - " + item.name;
                items[item.line[0] - 1].totalPrice += item.unitPrice * item.quantity;
              }
              break;
            case "combo":
            case "modifier":
              if (typeof items[item.line[0]] !== "undefined") { //pour fix bug (ishopfood-backend-v3 #267) api with sales from kiosk vs app (not same format)
                items[item.line[0]].extras.push(item);
                items[item.line[0]].totalPrice += item.unitPrice * item.quantity;
              } else {
                items[item.line[0] - 1].extras.push(item);
                items[item.line[0] - 1].totalPrice += item.unitPrice * item.quantity;
              }
              break;
          }
        }
        item.totalPrice = item.totalPrice * item.quantity;
      }

      //TODO
      //getSaleInventoryItems et passer les ids des items pour aller chercher les items de l'inventaire
      //aller chercher les images des items avec ça

      return items;
    },

    initializeMapPlatform() {
      /*this.platform = new H.service.Platform({
          apikey: "YcweJ0n-XSvKeqB81LELcNfh8kCiZce2Uj_LOUqWhdg"
        });

      this.loadMap();*/
    },

    loadMap() {
      let defaultLayers = this.platform.createDefaultLayers();
      setTimeout(() => {
        let element = document.getElementById("map");
        let position = {lat: parseFloat(this.sale.deliveryData.lat), lng: parseFloat(this.sale.deliveryData.lng)};
        if (element && !element.classList.contains("initialized")) {
          let map = new H.Map(
              element,
              defaultLayers.normal.map, {
                center: position,
                zoom: 10,
              }
          );
          let behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(map));
          let ui = H.ui.UI.createDefault(map, defaultLayers);
          element.classList.add("initialized");
          window.addEventListener('resize', () => {
            map.getViewPort().resize();
          });
        }
      }, 0);
    },

    viewCustomer() {
      this.$router.push(this.$router.buildContext("/customer", {id: this.sale.userId}));
    },

    print() {
      this.$refs.print.print();
    },

    async setSaleAsImported() {
      let response = await showConfirmation(this.$t("warning"), this.$t("sales.import_pos_manually.confirmation"));
      if (!response) {
        return;
      }
      showSpinner();
      let data = {
        posFailed: null,
      };
      let sale = await this.saveSale({saleId: this.sale.id, data});
      hideSpinner();
      if (sale) {
        this.sale = sale;
        toast(this.$t("sales.import_pos_manually.success"), "success");
      } else {
        toast(this.$t("sales.import_pos_manually.failed"), "error");
      }
    },

  }
}
</script>

<style lang="scss" scoped>

#map {
  min-height: 400px;
  height: 400px;
  margin-top: 10px;
}

.buttons {
  button:not(:last-child) {
    margin-right: 20px;
  }
}

.upper {
  text-transform: uppercase
}

.invoiceBlock {
  margin-left: 10px;

  > div {
    margin-bottom: 0;
  }

  .seatBlock {
    > label {
      font-weight: bold;
      width: 100%;
      display: block;
      color: inherit;
    }

    .simpleItemLine {
      border-bottom: none !important;

      > div {
        margin-bottom: 0;
      }
    }
  }

  .simpleItemLine {
    display: flex;
    justify-content: space-between;
    padding-top: 10px;

    > div {
      margin-bottom: 10px;
    }

    &:not(:last-child) {
      border-bottom: 1px solid #d9d9d9;
    }

    .value {
      font-weight: bold;
    }
  }

  .simpleItemLine.big {
    font-weight: bold;
    font-size: 18px;
  }
}

.itemBlock {
  display: flex;
  padding: 20px;
  border: 1px solid #d9d9d9;
  border-radius: 5px;
  margin-bottom: 15px;

  .image {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: red;
    margin-right: 20px;
    flex-shrink: 0;
  }

  .info {
    flex-grow: 1;

    .top {
      display: flex;
      font-size: 20px;
      font-weight: bold;
      border-bottom: 1px solid #e6e6e6;
      padding-bottom: 6px;
      margin-bottom: 8px;

      .name {
        flex-grow: 1;
      }
    }

    .extras {
      color: #808080;
      display: flex;
      flex-direction: column;

      .extra {
        display: flex;
        justify-content: space-between;

        &:not(:last-of-type) {
          margin-bottom: 4px;
        }
      }

      .separator {
        margin: 0 10px;
        width: 1px;
        background-color: #bfbfbf;
      }
    }
  }
}

.invoiceLabel {
  margin-left: 10px;
  margin-bottom: 10px;
}

.invoiceBlock {
  border: 1px solid #d9d9d9;
  border-radius: 5px;
  padding: 20px;
  margin-bottom: 20px;
}

.seatBlock {
  border-bottom: 1px solid #d9d9d9;
  padding-bottom: 10px;
  margin-bottom: 10px !important;
}

.sale__warning {
  .inner {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;

    .warning-text {
      flex-grow: 1;
    }
  }
}

.alert {
  color: red;
  font-weight: 500;
}

.refunded {
  font-weight: bold;
  padding: 20px;
  border: 1px solid #d9d9d9;
  border-radius: 5px;
  margin-bottom: 15px
}

.multiple-canvas {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;

  .canvas-holder {
    padding: 15px;
    border: 1px solid #e6e6e6;
    border-radius: 10px;
    margin: 7px;

    .filename {
      font-weight: bold;
      letter-spacing: 1px;
      font-size: 14px;
      text-align: center;
      margin-top: 5px;
      font-family: "Lato", sans-serif;
      color: black;
    }
  }
}

</style>
